import { useState } from 'react'
import { useNavigate } from 'react-router'
import { CalendarioSemana } from './calendario/CalendarioSemana'
import { CalendarioMes } from './calendario/CalendarioMes'
import { PanelDia } from './calendario/PanelDia'
import { useCalendarioSemana, type DiaSemana } from '../hooks/useCalendarioSemana'
import { useCalendarioMes } from '../hooks/useCalendarioMes'
import { colorDeDia } from './agendamiento/coloresDia'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'

/**
 * Calendario de repartos, con dos vistas: MES (rejilla tipo HEKKIO, por defecto)
 * y SEMANA (B.1). El mes es la vista de referencia pedida: rejilla con píldoras
 * (fondo=ruta, marca=estado) y leyenda; al pulsar un día se abre el panel del
 * día para ver los repartos y añadir uno nuevo (opción "ver + añadir").
 */

// lunes..viernes -> r1..r5; el fin de semana no tiene ruta de reparto (neutro).
const DIA_A_RUTA: Partial<Record<DiaSemana, DiaRuta>> = {
  lunes: 'r1',
  martes: 'r2',
  miercoles: 'r3',
  jueves: 'r4',
  viernes: 'r5',
}

function colorDeDiaSemana(dia: DiaSemana): string {
  const ruta = DIA_A_RUTA[dia]
  return ruta ? colorDeDia(ruta).clase : ''
}

type Vista = 'mes' | 'semana'

export function Calendario() {
  const navigate = useNavigate()
  const [vista, setVista] = useState<Vista>('mes')
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth())
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null)

  // Añadir un reparto = ir al agendamiento con ese día ya elegido. Se reutiliza
  // el flujo completo de agendar (resolver + publicar) en vez de duplicarlo.
  const irAAgendar = (iso: string) => navigate(`/agendamiento?fecha=${iso}`)

  return (
    <section className="flex flex-col gap-4 p-4">
      {/* Toggle mes/semana, como el conmutador de vistas de la referencia. */}
      <div className="flex items-center gap-1">
        <BotonVista activa={vista === 'mes'} onClick={() => setVista('mes')}>Mes</BotonVista>
        <BotonVista activa={vista === 'semana'} onClick={() => setVista('semana')}>Semana</BotonVista>
      </div>

      {vista === 'mes' ? (
        <VistaMes
          anio={anio}
          mes={mes}
          diaAbierto={diaAbierto}
          onCambiarMes={(a, m) => { setAnio(a); setMes(m); setDiaAbierto(null) }}
          onSeleccionarDia={setDiaAbierto}
          onCerrarDia={() => setDiaAbierto(null)}
          onAnadir={irAAgendar}
        />
      ) : (
        <VistaSemana onAnadir={irAAgendar} />
      )}
    </section>
  )
}

function BotonVista({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={[
        'rounded-md px-3 py-1.5 text-sm font-medium ring-1 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        activa ? 'bg-acento text-acento-contraste ring-transparent' : 'text-tinta-2 ring-borde hover:bg-superficie',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function VistaMes({
  anio, mes, diaAbierto, onCambiarMes, onSeleccionarDia, onCerrarDia, onAnadir,
}: {
  anio: number
  mes: number
  diaAbierto: string | null
  onCambiarMes: (anio: number, mes: number) => void
  onSeleccionarDia: (iso: string) => void
  onCerrarDia: () => void
  onAnadir: (iso: string) => void
}) {
  const mesId = `${anio}-${String(mes + 1).padStart(2, '0')}`
  const { data, isPending, isError, error, refetch } = useCalendarioMes(mesId)

  if (isPending) return <Cargando mensaje="Cargando el mes" />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  return (
    <div className="flex flex-col gap-4">
      <CalendarioMes
        repartosPorFecha={data}
        anio={anio}
        mes={mes}
        onCambiarMes={onCambiarMes}
        onSeleccionarDia={onSeleccionarDia}
      />
      {diaAbierto ? (
        <PanelDia
          iso={diaAbierto}
          repartos={data[diaAbierto] ?? []}
          onAnadir={onAnadir}
          onCerrar={onCerrarDia}
        />
      ) : null}
    </div>
  )
}

function VistaSemana({ onAnadir }: { onAnadir: (iso: string) => void }) {
  const { data, isPending, isError, error, refetch } = useCalendarioSemana()

  if (isPending) return <Cargando mensaje="Cargando la semana" />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  return (
    <CalendarioSemana
      repartosPorDia={data}
      colorDeDia={colorDeDiaSemana}
      tituloSemana="Repartos de la semana"
      onSeleccionarDia={(dia) => {
        const iso = isoDeDiaSemana(dia)
        if (iso) onAnadir(iso)
      }}
    />
  )
}

/**
 * Fecha ISO del día de ruta (lunes..viernes) en la semana en curso. Se calcula
 * a partir de hoy: el lunes de esta semana + el desplazamiento del día.
 */
function isoDeDiaSemana(dia: DiaSemana): string | null {
  const offset: Partial<Record<DiaSemana, number>> = {
    lunes: 0, martes: 1, miercoles: 2, jueves: 3, viernes: 4,
  }
  const desp = offset[dia]
  if (desp === undefined) return null
  const hoy = new Date()
  // getDay(): 0=domingo..6=sábado. Lunes de esta semana:
  const diaSemana = hoy.getDay()
  const restaLunes = diaSemana === 0 ? 6 : diaSemana - 1
  const lunes = new Date(hoy)
  lunes.setDate(hoy.getDate() - restaLunes + desp)
  const y = lunes.getFullYear()
  const m = String(lunes.getMonth() + 1).padStart(2, '0')
  const d = String(lunes.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
